import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../../config";
import LoadingScreen from "../common/LoadingScreen";
import { authFetch, clearAuth } from "../../utils/auth";
import "./../../assets/css/nav.css";

const PROVIDERS_CONFIG = [
    { id: "openai", name: "OpenAI", placeholder: "sk-...", desc: "GPT-5.5, GPT-5.2, GPT-5 Mini" },
    { id: "gemini", name: "Google Gemini", placeholder: "AIza...", desc: "Gemini 3.8 Flash, Gemini 3.1 Pro" },
    { id: "anthropic", name: "Anthropic Claude", placeholder: "sk-ant-...", desc: "Claude Sonnet 5.5, Opus 5.5, Haiku 5.5" },
    { id: "grok", name: "xAI (Grok)", placeholder: "xai-...", desc: "Grok 4.7, Grok 4.3, Grok 4.20" },
    { id: "custom", name: "Custom / Compatible", placeholder: "Optional API Key", desc: "Ollama, OpenRouter, DeepSeek" },
];

const LANGUAGE_META = {
    0: { name: "German", flag: "de.svg" },
    1: { name: "Mexican Spanish", flag: "mx.svg" },
    2: { name: "French", flag: "fr.svg" },
    3: { name: "Russian", flag: "ru.svg" },
    4: { name: "Brazilian Portuguese", flag: "br.svg" }
};

function SettingsModal({ isOpen, onClose, userId, registeredCourses, onRefresh }) {
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [selectedLanguageToAdd, setSelectedLanguageToAdd] = useState("");
    const [message, setMessage] = useState("");
    const [pendingRemoveLangId, setPendingRemoveLangId] = useState(null);
    const [maskedApiKey, setMaskedApiKey] = useState(null);
    const [listeningSettings, setListeningSettings] = useState(null);
    const [listeningSaving, setListeningSaving] = useState(false);

    // Multi-provider LLM state
    const [llmSummary, setLlmSummary] = useState(null);
    const [providerKeyInputs, setProviderKeyInputs] = useState({});
    const [customBaseUrl, setCustomBaseUrl] = useState("");
    const [savingProvider, setSavingProvider] = useState(null);
    const [customModelInput, setCustomModelInput] = useState("");
    const [isCustomModelMode, setIsCustomModelMode] = useState(false);

    // Password change state
    const [currentPwd, setCurrentPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [pwdStatus, setPwdStatus] = useState({ error: "", success: "", loading: false });

    // Account deletion state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletePwd, setDeletePwd] = useState("");
    const [deleteStatus, setDeleteStatus] = useState({ error: "", loading: false });

    useEffect(() => {
        if (!isOpen) return;

        authFetch(`${API_BASE_URL}/api/user/openai-key`)
            .then(res => res.json())
            .then(data => setMaskedApiKey(data.has_key ? data.masked_key : null))
            .catch(err => console.error("Error fetching API key status:", err));

        authFetch(`${API_BASE_URL}/api/user/llm-keys`)
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setLlmSummary(data);
                    if (data.providers?.openai?.masked_key) {
                        setMaskedApiKey(data.providers.openai.masked_key);
                    }
                    if (data.providers?.custom?.base_url) {
                        setCustomBaseUrl(data.providers.custom.base_url);
                    }
                }
            })
            .catch(err => console.error("Error fetching LLM keys:", err));

        // Fetch the user's general settings (everything except the API key)
        authFetch(`${API_BASE_URL}/api/user/settings`)
            .then(res => res.json())
            .then(data => setListeningSettings(data.settings || null))
            .catch(err => console.error("Error fetching user settings:", err));

        // Fetch all languages from backend
        authFetch(`${API_BASE_URL}/api/languages`)
            .then(res => res.json())
            .then(data => {
                const registeredIds = new Set(registeredCourses.map(c => parseInt(c.language_id, 10)));
                const notRegistered = data.languages.filter(lang => !registeredIds.has(parseInt(lang.id, 10)));
                setAvailableLanguages(notRegistered);
                if (notRegistered.length > 0) {
                    setSelectedLanguageToAdd(notRegistered[0].id.toString());
                } else {
                    setSelectedLanguageToAdd("");
                }
            })
            .catch(err => console.error("Error fetching languages:", err));

        setMessage("");
        setPendingRemoveLangId(null);
    }, [isOpen, registeredCourses, userId]);

    if (!isOpen) return null;

    const handleAddLanguage = () => {
        if (!selectedLanguageToAdd) return;

        authFetch(`${API_BASE_URL}/api/user/add-language`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                language_id: parseInt(selectedLanguageToAdd, 10)
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setMessage("Language added successfully!");
                    onRefresh();
                } else {
                    setMessage(data.error || "Failed to add language");
                }
            })
            .catch(err => {
                console.error("Error adding language:", err);
                setMessage("Failed to add language");
            });
    };

    const confirmRemoveLanguage = () => {
        if (pendingRemoveLangId === null) return;
        const langId = pendingRemoveLangId;
        setPendingRemoveLangId(null);

        authFetch(`${API_BASE_URL}/api/user/remove-language`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                language_id: parseInt(langId, 10)
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setMessage("Language removed successfully.");
                    onRefresh();
                } else {
                    setMessage(data.error || "Failed to remove language");
                }
            })
            .catch(err => {
                console.error("Error removing language:", err);
                setMessage("Failed to remove language");
            });
    };

    const saveProviderKey = (provider, keyToSave = null) => {
        setSavingProvider(provider);
        setMessage("");

        const apiKey = keyToSave !== null ? keyToSave : (providerKeyInputs[provider] || "");
        const payload = {
            provider,
            api_key: apiKey
        };
        if (provider === "custom") {
            payload.base_url = customBaseUrl;
        }

        authFetch(`${API_BASE_URL}/api/user/llm-key`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setMessage(`${provider.toUpperCase()} key ${data.configured ? "verified and saved." : "removed."}`);
                    setProviderKeyInputs(prev => ({ ...prev, [provider]: "" }));
                    // Refresh summary
                    authFetch(`${API_BASE_URL}/api/user/llm-keys`)
                        .then(r => r.json())
                        .then(d => {
                            if (d.ok) {
                                setLlmSummary(d);
                                if (provider === "openai") {
                                    setMaskedApiKey(d.providers?.openai?.masked_key || null);
                                }
                            }
                        });
                } else {
                    setMessage(data.error || `Failed to save ${provider} key`);
                }
            })
            .catch(err => {
                console.error("Error saving provider key:", err);
                setMessage(`Failed to verify or save ${provider} key`);
            })
            .finally(() => setSavingProvider(null));
    };

    const handleActiveProviderChange = (provider) => {
        authFetch(`${API_BASE_URL}/api/user/llm-active`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                provider,
                model: ""
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setLlmSummary(prev => prev ? ({ ...prev, active_provider: data.active_provider, active_model: data.active_model }) : null);
                    setIsCustomModelMode(false);
                    setMessage(`Active AI provider set to ${provider.toUpperCase()}`);
                }
            })
            .catch(err => console.error("Error setting active provider:", err));
    };

    const handleActiveModelChange = (model) => {
        if (model === "__custom__") {
            setIsCustomModelMode(true);
            return;
        }
        setIsCustomModelMode(false);
        authFetch(`${API_BASE_URL}/api/user/llm-active`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                model
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    setLlmSummary(prev => prev ? ({ ...prev, active_model: data.active_model }) : null);
                    setMessage(`Active model set to ${model || "provider default"}`);
                }
            })
            .catch(err => console.error("Error setting active model:", err));
    };

    const updateListeningSetting = (key, value) => {
        setListeningSettings(prev => ({ ...prev, [key]: value === "" ? "" : parseInt(value, 10) }));
    };

    const saveListeningSettings = () => {
        if (!listeningSettings) return;
        setListeningSaving(true);

        authFetch(`${API_BASE_URL}/api/user/settings`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                settings: listeningSettings
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.ok) {
                    // The server coerces and clamps, so its values win.
                    setListeningSettings(data.settings);
                    setMessage("Listening settings saved.");
                } else {
                    setMessage(data.error || "Failed to save settings");
                }
            })
            .catch(err => {
                console.error("Error saving settings:", err);
                setMessage("Failed to save settings");
            })
            .finally(() => setListeningSaving(false));
    };

    const listeningRangeInvalid = !!listeningSettings && (
        !listeningSettings.sual_min_words ||
        !listeningSettings.sual_max_words ||
        listeningSettings.sual_min_words > listeningSettings.sual_max_words
    );

    const handleRemoveLanguageClick = (langId) => {
        if (registeredCourses.length <= 1) {
            setMessage("You must keep at least one registered language.");
            return;
        }
        setPendingRemoveLangId(langId);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwdStatus({ error: "", success: "", loading: true });
        if (!currentPwd || !newPwd) {
            setPwdStatus({ error: "Please enter both current and new password.", success: "", loading: false });
            return;
        }
        if (newPwd.length < 6) {
            setPwdStatus({ error: "New password must be at least 6 characters.", success: "", loading: false });
            return;
        }
        if (newPwd !== confirmPwd) {
            setPwdStatus({ error: "New passwords do not match.", success: "", loading: false });
            return;
        }

        try {
            const res = await authFetch(`${API_BASE_URL}/api/user/change-password`, {
                method: "POST",
                body: JSON.stringify({ current_password: currentPwd, new_password: newPwd })
            });
            const data = await res.json();
            if (res.ok) {
                setPwdStatus({ error: "", success: "Password changed successfully!", loading: false });
                setCurrentPwd("");
                setNewPwd("");
                setConfirmPwd("");
            } else {
                setPwdStatus({ error: data.error || "Failed to change password.", success: "", loading: false });
            }
        } catch {
            setPwdStatus({ error: "Could not connect to server.", success: "", loading: false });
        }
    };

    const handleDeleteAccount = async () => {
        if (!deletePwd) {
            setDeleteStatus({ error: "Please enter your password to confirm.", loading: false });
            return;
        }
        setDeleteStatus({ error: "", loading: true });

        try {
            const res = await authFetch(`${API_BASE_URL}/api/user/delete-account`, {
                method: "POST",
                body: JSON.stringify({ password: deletePwd })
            });
            const data = await res.json();
            if (res.ok) {
                clearAuth();
                window.location.href = "/";
            } else {
                setDeleteStatus({ error: data.error || "Failed to delete account.", loading: false });
            }
        } catch {
            setDeleteStatus({ error: "Could not connect to server.", loading: false });
        }
    };

    const isErrorMessage = message && (
        message.toLowerCase().includes("failed") ||
        message.toLowerCase().includes("must") ||
        message.toLowerCase().includes("error") ||
        message.toLowerCase().includes("invalid")
    );

    return (
        <div className="settings-modal-backdrop" onClick={onClose}>
            <div className="settings-modal-content" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="settings-modal-title">
                <div className="settings-modal-header">
                    <h2 id="settings-modal-title" className="settings-modal-title">Account Settings</h2>
                    <button
                        type="button"
                        className="settings-close-btn"
                        onClick={onClose}
                        aria-label="Close settings"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div className="settings-modal-body">
                    {message && (
                        <div className={`settings-alert ${isErrorMessage ? "settings-alert--error" : "settings-alert--success"}`}>
                            {message}
                        </div>
                    )}

                    {pendingRemoveLangId !== null && (
                        <div className="settings-confirm-card">
                            <h4 className="settings-confirm-title">Remove Course?</h4>
                            <p className="settings-confirm-desc">
                                Are you sure you want to unregister from <strong>{LANGUAGE_META[pendingRemoveLangId]?.name}</strong>?
                                Your learning progress will be saved, but the course will no longer appear on your dashboard.
                            </p>
                            <div className="settings-confirm-actions">
                                <button
                                    type="button"
                                    className="settings-btn settings-btn--danger"
                                    onClick={confirmRemoveLanguage}
                                >
                                    Yes, Remove
                                </button>
                                <button
                                    type="button"
                                    className="settings-btn settings-btn--secondary"
                                    onClick={() => setPendingRemoveLangId(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Section 1: My Languages */}
                    <section className="settings-section">
                        <h3 className="settings-section-title">My Languages</h3>
                        <p className="settings-help-text">You are currently studying the following languages:</p>
                        <div className="settings-language-list">
                            {registeredCourses.map(course => {
                                const langId = parseInt(course.language_id, 10);
                                const meta = LANGUAGE_META[langId];
                                return (
                                    <div key={course.id} className="settings-language-item">
                                        <div className="settings-language-item-left">
                                            {meta?.flag && (
                                                <img
                                                    src={require(`../../assets/images/${meta.flag}`)}
                                                    alt=""
                                                    className="settings-language-flag"
                                                />
                                            )}
                                            <span className="settings-language-name">{meta?.name || course.language}</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="settings-btn settings-btn--danger-soft settings-btn--sm"
                                            onClick={() => handleRemoveLanguageClick(langId)}
                                            disabled={registeredCourses.length <= 1}
                                            title={registeredCourses.length <= 1 ? "Cannot remove last language" : "Remove language"}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Section 2: Add Language */}
                    <section className="settings-section">
                        <h3 className="settings-section-title">Add a Course</h3>
                        <p className="settings-help-text">Select a new language to add to your account:</p>
                        {availableLanguages.length > 0 ? (
                            <div className="settings-form-row settings-form-row--inline">
                                <select
                                    className="settings-select"
                                    value={selectedLanguageToAdd}
                                    onChange={e => setSelectedLanguageToAdd(e.target.value)}
                                    aria-label="Select language to add"
                                >
                                    {availableLanguages.map(lang => (
                                        <option key={lang.id} value={lang.id}>
                                            {LANGUAGE_META[lang.id]?.name || lang.language}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="settings-btn settings-btn--primary"
                                    onClick={handleAddLanguage}
                                >
                                    Add Course
                                </button>
                            </div>
                        ) : (
                            <div className="settings-empty-state">You are registered for all available languages!</div>
                        )}
                    </section>

                    {/* Section 3: Shut Up & Listen Settings */}
                    <section className="settings-section">
                        <h3 className="settings-section-title">Shut Up &amp; Listen</h3>
                        <p className="settings-help-text">
                            Choose sentence lengths and speech rate. Shorter is easier to catch; longer trains you to follow real speech.
                        </p>
                        {listeningSettings ? (
                            <div className="settings-card-group">
                                <div className="settings-card">
                                    <div className="settings-card-header">
                                        <span className="settings-card-label">Sentence Length</span>
                                    </div>
                                    <div className="settings-grid settings-grid--2col">
                                        <div className="settings-field">
                                            <label className="settings-field-label" htmlFor="sual-min-words">
                                                Shortest
                                            </label>
                                            <div className="settings-input-group">
                                                <input
                                                    id="sual-min-words"
                                                    type="number"
                                                    className="settings-input"
                                                    min="2"
                                                    max="20"
                                                    value={listeningSettings.sual_min_words ?? ""}
                                                    onChange={e => updateListeningSetting("sual_min_words", e.target.value)}
                                                />
                                                <span className="settings-input-addon">words</span>
                                            </div>
                                        </div>
                                        <div className="settings-field">
                                            <label className="settings-field-label" htmlFor="sual-max-words">
                                                Longest
                                            </label>
                                            <div className="settings-input-group">
                                                <input
                                                    id="sual-max-words"
                                                    type="number"
                                                    className="settings-input"
                                                    min="2"
                                                    max="30"
                                                    value={listeningSettings.sual_max_words ?? ""}
                                                    onChange={e => updateListeningSetting("sual_max_words", e.target.value)}
                                                />
                                                <span className="settings-input-addon">words</span>
                                            </div>
                                        </div>
                                    </div>
                                    {listeningRangeInvalid && (
                                        <div className="settings-field-error">
                                            The shortest length must not be greater than the longest.
                                        </div>
                                    )}
                                    <div className="settings-card-actions">
                                        <button
                                            type="button"
                                            className="settings-btn settings-btn--primary settings-btn--sm"
                                            onClick={saveListeningSettings}
                                            disabled={listeningSaving || listeningRangeInvalid}
                                        >
                                            {listeningSaving ? "Saving..." : "Save Lengths"}
                                        </button>
                                    </div>
                                </div>

                                <div className="settings-card">
                                    <div className="settings-slider-header">
                                        <label className="settings-card-label" htmlFor="sual-rate-setting">
                                            Speaking Speed
                                        </label>
                                        <span className="settings-badge-pill">
                                            {(listeningSettings.sual_speech_rate ?? 1).toFixed(2)}x
                                        </span>
                                    </div>
                                    <div className="settings-slider-row">
                                        <span className="settings-slider-bound">0.5x</span>
                                        <input
                                            id="sual-rate-setting"
                                            type="range"
                                            className="settings-range-slider"
                                            min="0.5"
                                            max="1.6"
                                            step="0.05"
                                            value={listeningSettings.sual_speech_rate ?? 1}
                                            onChange={e => setListeningSettings(prev => ({
                                                ...prev,
                                                sual_speech_rate: parseFloat(e.target.value)
                                            }))}
                                        />
                                        <span className="settings-slider-bound">1.6x</span>
                                    </div>
                                    <p className="settings-field-note">
                                        You can also adjust speaking speed mid-game from the in-game settings.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <LoadingScreen inline title="Loading settings..." size={28} />
                        )}
                    </section>

                    {/* Section 4: AI Provider & Model Settings */}
                    <section className="settings-section">
                        <h3 className="settings-section-title">AI &amp; Model Settings</h3>
                        <p className="settings-help-text">
                            Connect your preferred AI provider to unlock AI-powered features (sentence generation, live evaluation, and lyrics translation).
                            Keys are stored securely and verified before saving.
                        </p>

                        {/* Active Provider & Model Selection Card */}
                        <div className="settings-card" style={{ marginBottom: "14px" }}>
                            <div className="settings-grid settings-grid--2col">
                                <div className="settings-field">
                                    <label className="settings-field-label" htmlFor="active-ai-provider-select">
                                        Active AI Provider
                                    </label>
                                    <select
                                        id="active-ai-provider-select"
                                        className="settings-select"
                                        value={llmSummary?.active_provider || "openai"}
                                        onChange={e => handleActiveProviderChange(e.target.value)}
                                        aria-label="Active AI Provider"
                                    >
                                        {PROVIDERS_CONFIG.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} {llmSummary?.providers?.[p.id]?.configured ? "(Configured)" : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="settings-field">
                                    <label className="settings-field-label" htmlFor="active-ai-model-select">
                                        Active Model
                                    </label>
                                    <select
                                        id="active-ai-model-select"
                                        className="settings-select"
                                        value={isCustomModelMode ? "__custom__" : (llmSummary?.active_model || "")}
                                        onChange={e => handleActiveModelChange(e.target.value)}
                                        aria-label="Active Model"
                                    >
                                        <option value="">Default Recommended</option>
                                        {(llmSummary?.provider_models?.[llmSummary?.active_provider || "openai"] || []).map(m => (
                                            <option key={m.id} value={m.id}>
                                                {m.name || m.id}
                                            </option>
                                        ))}
                                        <option value="__custom__">Custom model name...</option>
                                    </select>
                                </div>
                            </div>

                            {isCustomModelMode && (
                                <div className="settings-form-row settings-form-row--inline" style={{ marginTop: "10px" }}>
                                    <input
                                        type="text"
                                        className="settings-input"
                                        placeholder="Enter custom model name (e.g. llama3-70b, deepseek-chat)"
                                        value={customModelInput}
                                        onChange={e => setCustomModelInput(e.target.value)}
                                        aria-label="Custom model identifier"
                                    />
                                    <button
                                        type="button"
                                        className="settings-btn settings-btn--primary settings-btn--sm"
                                        onClick={() => handleActiveModelChange(customModelInput.trim())}
                                        disabled={!customModelInput.trim()}
                                    >
                                        Apply Model
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Individual Provider Cards */}
                        <div className="settings-provider-list">
                            {PROVIDERS_CONFIG.map(prov => {
                                const provData = llmSummary?.providers?.[prov.id] || {};
                                const isConfigured = Boolean(provData.configured) || (prov.id === "openai" && Boolean(maskedApiKey));
                                const isActive = (llmSummary?.active_provider || "openai") === prov.id;
                                const masked = prov.id === "openai" ? (maskedApiKey || provData.masked_key) : provData.masked_key;
                                const isSaving = savingProvider === prov.id;

                                return (
                                    <div
                                        key={prov.id}
                                        className={`settings-provider-card ${isActive ? "settings-provider-card--active" : ""}`}
                                    >
                                        <div className="settings-provider-card-header">
                                            <div className="settings-provider-title-wrap">
                                                <h4 className="settings-provider-title">{prov.name} API Key</h4>
                                                {isActive ? (
                                                    <span className="settings-status-badge settings-status-badge--active">Active</span>
                                                ) : isConfigured ? (
                                                    <span className="settings-status-badge settings-status-badge--configured">Saved</span>
                                                ) : (
                                                    <span className="settings-status-badge settings-status-badge--unset">Not Set</span>
                                                )}
                                            </div>
                                            {isConfigured && (
                                                <button
                                                    type="button"
                                                    className="settings-btn settings-btn--danger-soft settings-btn--sm"
                                                    onClick={() => saveProviderKey(prov.id, "")}
                                                    disabled={isSaving}
                                                    title={`Remove ${prov.name} API key`}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        {isConfigured && masked && (
                                            <div className="settings-key-active-card" style={{ marginBottom: "10px" }}>
                                                <span className="settings-key-masked">{masked}</span>
                                            </div>
                                        )}

                                        {prov.id === "custom" && (
                                            <div style={{ marginBottom: "8px" }}>
                                                <label className="settings-field-label" style={{ fontSize: "12px", marginBottom: "4px", display: "block" }}>
                                                    Base URL (e.g. http://localhost:11434/v1 or https://openrouter.ai/api/v1)
                                                </label>
                                                <input
                                                    type="text"
                                                    className="settings-input"
                                                    placeholder="http://localhost:11434/v1"
                                                    value={customBaseUrl}
                                                    onChange={e => setCustomBaseUrl(e.target.value)}
                                                    aria-label="Custom endpoint base URL"
                                                />
                                            </div>
                                        )}

                                        <div className="settings-form-row settings-form-row--inline">
                                            <input
                                                type="password"
                                                className="settings-input"
                                                placeholder={isConfigured ? "Enter new key to replace" : prov.placeholder}
                                                value={providerKeyInputs[prov.id] || ""}
                                                autoComplete="off"
                                                onChange={e => setProviderKeyInputs(prev => ({ ...prev, [prov.id]: e.target.value }))}
                                                onKeyDown={e => { if (e.key === "Enter") saveProviderKey(prov.id); }}
                                                aria-label={`${prov.name} API key`}
                                            />
                                            <button
                                                type="button"
                                                className="settings-btn settings-btn--primary"
                                                onClick={() => saveProviderKey(prov.id)}
                                                disabled={isSaving || (!(providerKeyInputs[prov.id] || "").trim() && (prov.id !== "custom" || !customBaseUrl.trim()))}
                                            >
                                                {isSaving ? "Verifying..." : "Save Key"}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Section 5: Account Security */}
                    <section className="settings-section settings-section--last">
                        <h3 className="settings-section-title">Account Security</h3>
                        <p className="settings-help-text">
                            Manage your login credentials or delete your account data.
                        </p>

                        <div className="settings-card" style={{ marginBottom: "16px" }}>
                            <h4 style={{ margin: "0 0 12px 0", fontSize: "15px", fontWeight: "600" }}>Change Password</h4>
                            <form onSubmit={handleChangePassword}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                    <input
                                        type="password"
                                        className="settings-input"
                                        placeholder="Current Password"
                                        value={currentPwd}
                                        onChange={e => setCurrentPwd(e.target.value)}
                                    />
                                    <input
                                        type="password"
                                        className="settings-input"
                                        placeholder="New Password (min 6 characters)"
                                        value={newPwd}
                                        onChange={e => setNewPwd(e.target.value)}
                                    />
                                    <input
                                        type="password"
                                        className="settings-input"
                                        placeholder="Confirm New Password"
                                        value={confirmPwd}
                                        onChange={e => setConfirmPwd(e.target.value)}
                                    />
                                    {pwdStatus.error && (
                                        <div style={{ color: "#ef4444", fontSize: "13px" }}>{pwdStatus.error}</div>
                                    )}
                                    {pwdStatus.success && (
                                        <div style={{ color: "#10b981", fontSize: "13px" }}>{pwdStatus.success}</div>
                                    )}
                                    <button
                                        type="submit"
                                        className="settings-btn settings-btn--primary"
                                        style={{ alignSelf: "flex-start", marginTop: "4px" }}
                                        disabled={pwdStatus.loading || !currentPwd || !newPwd}
                                    >
                                        {pwdStatus.loading ? "Updating..." : "Update Password"}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="settings-card" style={{ borderColor: "#fee2e2", backgroundColor: "#fff5f5" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                    <h4 style={{ margin: "0", fontSize: "15px", fontWeight: "600", color: "#991b1b" }}>
                                        Delete Account
                                    </h4>
                                    <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#7f1d1d" }}>
                                        Permanently delete your account and all learning progress.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="settings-btn settings-btn--danger-soft settings-btn--sm"
                                    onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                                >
                                    {showDeleteConfirm ? "Cancel" : "Delete Account"}
                                </button>
                            </div>

                            {showDeleteConfirm && (
                                <div style={{ marginTop: "14px", paddingTop: "14px", borderTop: "1px solid #fecaca" }}>
                                    <p style={{ margin: "0 0 8px 0", fontSize: "13px", fontWeight: "500", color: "#991b1b" }}>
                                        Please enter your password to confirm permanent deletion:
                                    </p>
                                    <div className="settings-form-row settings-form-row--inline">
                                        <input
                                            type="password"
                                            className="settings-input"
                                            placeholder="Your password"
                                            value={deletePwd}
                                            onChange={e => setDeletePwd(e.target.value)}
                                            style={{ borderColor: "#fca5a5" }}
                                        />
                                        <button
                                            type="button"
                                            className="settings-btn settings-btn--danger"
                                            onClick={handleDeleteAccount}
                                            disabled={deleteStatus.loading || !deletePwd}
                                            style={{ backgroundColor: "#dc2626", color: "#ffffff" }}
                                        >
                                            {deleteStatus.loading ? "Deleting..." : "Permanently Delete"}
                                        </button>
                                    </div>
                                    {deleteStatus.error && (
                                        <div style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px" }}>
                                            {deleteStatus.error}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                <div className="settings-modal-footer">
                    <button type="button" className="settings-btn settings-btn--secondary settings-btn--lg" onClick={onClose}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SettingsModal;
